import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Form, List, Modal, Table, Popup, Message } from "semantic-ui-react";
import { DatePicker } from "../../../components/shared/DatePicker";
import WeekDaysSelect from "../../../components/shared/WeekDaysSelect";
import { useMessageResources, useTextResources } from "../../../language";
import PropTypes from "prop-types";
import moment from "moment";
import {
  handleStartEndChange,
  makeDaysOfWeekArrayIntoInt,
  filterDateRangesByDateRange,
  validateDateRange,
  validateDateRangeMessage,
  toServerDateFormat
} from "../../../components/shared/utils";
import { PriceListContext } from "../context";
import { useCurrentHotel } from "../../../hooks/useCurrentHotel";
import { PriceLists } from "../../../api/services/next";
import EntityTypes from "../../GroupReservation/Constants/EntityTypes";
import {  Designations, RatePricingTypesEnum } from "../../../components/shared/constants";
import NumericInput from "../../../components/shared/NumericInput";
import { onError, onSuccess, onWarning } from "../../../components/shared/messages";
import { useConfirm } from "../../../hooks/useConfirm";
import CheckBoxToggle from "../../../components/shared/CheckBoxToggle";
import { RPEsRearangeDateRanges, RPEsEditRearangeDateranges, getDateRangeString } from "./pricingUtils";
import PricingElementPanel from "../../../components/shared/PricingElementPanel";
import { PriceElementsList } from "./PriceElementsList";
import { useLoader } from "../../../hooks/useLoader";

export const SupplementPopup = ({ open, onClose, roomTypes, standardRoomTypeId, updateRPEs, Packages,
  rateElementName }) => {
  const loader = useLoader();
  const currentHotel = useCurrentHotel();
  const text = useTextResources();
  const message = useMessageResources();
  const confirm = useConfirm();
  const { state } = useContext(PriceListContext);
  const [groupedPricingElements, setGroupedPricingElements] = useState({});
  const [fromFilterDate, setFromFilterDate] = useState();
  const [toFilterDate, setToFilterDate] = useState();
  const [startDate, setStartDate] = useState(moment(state.StartDate).startOf("day").toDate());
  const [endDate, setEndDate] = useState(moment(state.EndDate).startOf("day").toDate());
  const [selectedDays, setSelectedDays] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  const tableHeaders = useMemo(() => [
    text("RoomType"),`${text("Amount")}/${text("Percent")}`, text("Single"), text("Double"), text("ExtraAdult"), text("ExtraChild"), text("ExtraInfant")
  ], [text]);

  const cellDesignations = useMemo(() => [
    Designations.Single, Designations.Double, Designations.FirstExtraAdult, Designations.FirstExtraChild, Designations.FirstExtraInfant
  ], []);

  const priceToUpdate = useMemo(() => {
    return state?.IsProductPricingIncludesTax ? "AmountAfterTax" : "AmountBeforeTax";
  }, [state?.IsProductPricingIncludesTax]);

  useEffect(() => {
    if (open) {
      const RatePricingElements = state.RatePricingElements.filter(
        (rpe) =>
          rpe.EntityTypeId === EntityTypes.RoomType &&
          rpe.IsActive &&
          rpe.EntityId !== standardRoomTypeId &&
          Boolean(rpe.RatePricingElementsPackages?.length) === Boolean(Packages?.length)
      );
      const groupedRPEs = RatePricingElements.reduce((accumulator, rpe) => {
        const dateRangeString = getDateRangeString(rpe, state.Id);
        const dateRangeComponents = dateRangeString.split("$");
        const fittingRPP = rpe.RatePerPeople.find(x => !x.ExceptionDate);
        if (fittingRPP) {
          rpe.RatePerPeople = [fittingRPP, ...rpe.RatePerPeople.filter(rpe => rpe.ExceptionDate)];
        }
        else {
          rpe.RatePerPeople = [{
            RatePricingElementId: rpe.Id,
            RatePerPersonPrices: [],
            RateId: state.Id,
            RoomTypeId: rpe.EntityId,
          }];
        }
        if (accumulator[dateRangeString]) {
          accumulator[dateRangeString].RPEs.push(rpe);
        } else {
          accumulator[dateRangeString] = {
            Id: dateRangeString,
            StartDate: new Date(dateRangeComponents[0]),
            EndDate: new Date(dateRangeComponents[1]),
            Weekdays: parseInt(dateRangeComponents[2]),
            RPEs: [rpe],
            EntityTypeId: EntityTypes.RoomType,
            EntityId: standardRoomTypeId,
            IsActive: true,
            RateId: rpe.RateId
          };
        }
        return accumulator;
      }, {});
      setGroupedPricingElements(groupedRPEs);
    }
    else {
      setGroupedPricingElements({});
      setIsDirty(false);
    }
  }, [state.RatePricingElements, open]);

  const onSave = async (isClose) => {
    loader(true);
    let allRPEs = Object.values(groupedPricingElements).reduce((accumulator, group) => [
      ...accumulator,
      ...group.RPEs.map(x => ({
        ...x,
        IsActive: group.IsActive,
        IsUpdated: group.RPEs?.find(rpe => rpe.Id == x.Id)?.IsUpdated,
        DoNotDistribute: group.DoNotDistribute,
        StartDate: toServerDateFormat(group.StartDate),
        EndDate: toServerDateFormat(group.EndDate),
        BaseEntityId: standardRoomTypeId,
        Weekdays: Array.isArray(group.Weekdays) ?
          makeDaysOfWeekArrayIntoInt(group.Weekdays) :
          group.Weekdays
      }))
    ], []);
    try {
      await PriceLists.UpdatePricingElements({
        StartDate: toServerDateFormat(state.StartDate),
        EndDate: toServerDateFormat(state.EndDate),
        RatePricingElements: allRPEs,
        HotelId: state.HotelId,
        CurrencyId: state.CurrencyId || currentHotel?.MainCurrencyId,
        IsProductPricingIncludesTax: state?.IsProductPricingIncludesTax,
        IsDistributeToChannelManager: state.IsDistributeToChannelManager,
        RateCategoryId: state.RateCategoryId,
        Id: state.Id,
        BaseRateId: state.BaseRateId
      });
      await updateRPEs();
      onSuccess();
      setIsDirty(false);
      isClose && onClose();
    } catch (error) {
      error.status === 400 ? onWarning(error) : onError(error);
    } finally {
      loader(false);
    }
  };

  const changePriceInRoomType = (value, designationId, rpe, group, roomType) => {
    if (!value && value != 0) return;
    setIsDirty(true);
    if (!rpe) {
      group.RPEs.push({
        Id: 0,
        TempId: `newRpeForGroupTempId-${crypto.randomUUID()}`,
        StartDate: group.StartDate,
        EndDate: group.EndDate,
        Weekdays: selectedDays,
        RatePerPeople: [{
          RatePricingElementId: 0,
          RatePerPersonPrices: [{
            Id: 0,
            RatePerPersonId: 0,
            DesignationId: designationId,
            AmountAfterTax: 0,
            AmountBeforeTax: 0,
            [priceToUpdate]: value
          }],
          RateId: state.Id,
          RoomTypeId: roomType.Id,
        }],
        PricingTemplateId: Object.values(groupedPricingElements)?.[0]?.RPEs?.[0].PricingTemplateId,
        RatePerPersonId: 0,
        RatePricingTypeId: RatePricingTypesEnum.PerPerson,
        RateId: state.Id,
        EntityId: roomType.Id,
        EntityTypeId: EntityTypes.RoomType,
        IsActive: true,
        IsNew: true
      })
      setGroupedPricingElements({ ...groupedPricingElements });
      return;
    }
    rpe.IsUpdated = true;
    let fittingPPPrice = rpe.RatePerPeople[0].RatePerPersonPrices.find(x => x.DesignationId === designationId);
    let nonDesignatedPrice = priceToUpdate === "AmountAfterTax" ? "AmountBeforeTax" : "AmountAfterTax";

    if (fittingPPPrice) {
      fittingPPPrice[priceToUpdate] = value;
      fittingPPPrice[nonDesignatedPrice] = rpe.IsPercent ? value : 0;
    }
    else {
      rpe.RatePerPeople[0].RatePerPersonPrices.push({
        Id: 0,
        RatePerPersonId: rpe.RatePerPeople[0].Id,
        DesignationId: designationId,
        AmountAfterTax: 0,
        AmountBeforeTax: 0,
        [priceToUpdate]: value,
        [nonDesignatedPrice]: rpe.IsPercent ? value : 0
      })
    }
    setGroupedPricingElements({ ...groupedPricingElements })
  };

  const toggleIsPercent = (fittingRPE) => {
    if (fittingRPE) {
      fittingRPE.IsPercent = !fittingRPE.IsPercent;
      fittingRPE.RatePerPeople[0].RatePerPersonPrices = fittingRPE.RatePerPeople[0].RatePerPersonPrices.map((price) => ({
        ...price,
        AmountAfterTax: 0,
        AmountBeforeTax: 0,
      }));
    }
    setGroupedPricingElements({ ...groupedPricingElements });
  };

  const createRPEsForRoomTypes = () => {
    return roomTypes.map(roomType => ({
      Id: 0,
      StartDate: startDate,
      EndDate: endDate,
      Weekdays: selectedDays,
      RatePerPeople: [{
        RatePricingElementId: 0,
        RatePerPersonPrices: [],
        RateId: state.Id,
        RoomTypeId: roomType.Id,
      }],
      PricingTemplateId: Object.values(groupedPricingElements)?.[0]?.RPEs?.[0].PricingTemplateId,
      RatePerPersonId: 0,
      RatePricingTypeId: RatePricingTypesEnum.PerPerson,
      RateId: state.Id,
      EntityId: roomType.Id,
      EntityTypeId: EntityTypes.RoomType,
      roomTypeName: roomType.Name,
      IsActive: true,
    }))
  }

  const memoDatesForWeekComponent = useMemo(
    () => (startDate && endDate ? [{ startDate, endDate }] : []),
    [startDate, endDate]
  );

  const addRPEsGroup = () => {
      const newPricingElementsGroup = {
      TempId: `newRpeForGroupTempId-${crypto.randomUUID()}`,
      StartDate: startDate,
      EndDate: endDate,
      Weekdays: selectedDays,
      RatePricingTypeId: 1,
      RateId: state.Id,
      IsActive: true,
      CreateDate: new Date(),
      IsNew: true,
      RPEs: createRPEsForRoomTypes(),
    };

    const newPricingElementGroups = RPEsRearangeDateRanges(newPricingElementsGroup, Object.values(groupedPricingElements), state.BaseRateId);
    setGroupedPricingElements(
      newPricingElementGroups.reduce(
        (accumulator, group) => ({
          ...accumulator,
          [getDateRangeString(group)]: group
        }), {})
    )

    setIsDirty(true);
  };

  const addCopiedRPEsGroup = origin => {
    const copiedRPEs = origin.RPEs.map(rpe => ({
      ...rpe,
      Id: 0,
      RatePerPersonId: 0,
      RatePerPeople: rpe.RatePerPeople
        .map(rpp => ({
          ...rpp,
          Id: 0,
          RatePricingElementId: 0,
          RatePerPersonPrices: rpp.RatePerPersonPrices.map(
            rppp => ({ ...rppp, Id: 0, RatePerPersonId: 0 })
          ),
        })),
    }));

    const newPricingElementsGroup = {
      TempId: `newRpeForGroupTempId-${crypto.randomUUID()}`,
      StartDate: null,
      EndDate: null,
      Weekdays: origin.Weekdays,
      RatePricingTypeId: 1,
      RateId: state.Id,
      IsActive: true,
      CreateDate: new Date(),
      IsNew: true,
      RPEs: [...copiedRPEs],
      isCopy: true,
      isEdit: true
    };
    setGroupedPricingElements(Object.values(groupedPricingElements).reduce((gs, g) => {
      gs[getDateRangeString(g)] = g;
      if (g.isOrigin) {
        gs[getDateRangeString(newPricingElementsGroup)] = (newPricingElementsGroup);
      }
      return gs;
    }, {}));

    setIsDirty(true);
  }


  const onCopy = (oldGroup) => {
    oldGroup.isOrigin = true;
    addCopiedRPEsGroup(oldGroup);
    setIsDirty(true)
  }

  const onEdit = (pricing) => {
    const newPricingElementGroups = RPEsEditRearangeDateranges(pricing, Object.values(groupedPricingElements), state.BaseRateId);
    setGroupedPricingElements(
      newPricingElementGroups.reduce(
        (accumulator, group) => ({
          ...accumulator,
          [getDateRangeString(group)]: { ...group, isEdit: false }
        }), {})
    )

    setIsDirty(true);
  }

  const onDelete = async (group) => {
    if (!(await confirm({}))) return;
    if (group.IsNew) {
      let newGroupedPricingElements = { ...groupedPricingElements };
      delete newGroupedPricingElements[getDateRangeString(group)];
      setGroupedPricingElements(newGroupedPricingElements);
    }
    else {
      group.IsActive = false;
      for (let rpe of group.RPEs)
        rpe.IsActive = false;
      setGroupedPricingElements({ ...groupedPricingElements });
    }
  };

  const getPrice = (fittingRPE, designationId) => {
    return fittingRPE?.RatePerPeople?.[0]?.RatePerPersonPrices.find(x => x.DesignationId === designationId)?.[priceToUpdate];
  }

  const handleClose = async () => {
    if (isDirty) {
      if (await confirm(
        {
          content: `${message("WouldYouLikeToContinue")}?`,
          confirmButton: {
            content: text('Yes'),
            positive: true,
          },
          cancelButton: {
            content: text('No'),
            negative: true,
          },
        }))
        onClose();
    }
    else onClose();
  }

  const onSaveGroup = (group) => {
    group.GroupBeforeEditing = group.prev;
    group.RPEs.forEach(rpe => {
      rpe.IsUpdated = true
    });
    onEdit(group);
  }

  return (
    <Modal size="large" open={open} dimmer centered className="price-list-settings">
      <Modal.Header>{`${text("EditSupplementPrices")} - ${rateElementName}`}</Modal.Header>
      <Modal.Content style={{maxHeight: 690, overflowY: "auto"}}>
        <Form warning={!validateDateRange(startDate, endDate, selectedDays) || !roomTypes?.length}>
        <Form.Group widths={4}>
            <DatePicker
              label={text("From")}
              onChange={(value) => handleStartEndChange([value, endDate], 0, setStartDate, setEndDate)}
              clearable={false}
              value={startDate}
              minDate={moment(state.StartDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
            />
            <DatePicker
              label={text("To")}
              onChange={(value) => handleStartEndChange([startDate, value], 1, setStartDate, setEndDate)}
              clearable={false}
              value={endDate}
              minDate={moment(startDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
            />
            <div className="weekdays-select">
              <WeekDaysSelect onChange={setSelectedDays} dates={memoDatesForWeekComponent} />
              <div className="add-pricing-button">
              <Button primary onClick={addRPEsGroup} disabled={!validateDateRange(startDate, endDate, selectedDays) || !roomTypes?.length }>
                {text("Add")}
              </Button>
              </div>
            </div>
          </Form.Group>
          <Message
            warning
            content={text("RoomTypes") + ": " + text("NothingSelected")}
            hidden={roomTypes?.length}
          />
          <Message
            warning
            content={text(validateDateRangeMessage(startDate, endDate, selectedDays))}
            hidden={validateDateRange(startDate, endDate, selectedDays)}
          />
          <Form.Group widths={4}>
            <DatePicker
              label={`${text("FilterBy")} ${text("FromDate")}`}
              value={fromFilterDate}
              onChange={(value) => handleStartEndChange([value, toFilterDate], 0, setFromFilterDate, setToFilterDate)}
              clearable={false}
            />
            <DatePicker
              label={`${text("FilterBy")} ${text("ToDate")}`}
              value={toFilterDate}
              onChange={(value) => handleStartEndChange([fromFilterDate, value], 1, setFromFilterDate, setToFilterDate)}
              clearable={false}
            />
            <div style={{ marginLeft: 5, alignSelf: "flex-end" }}>
              <Button primary onClick={() => {
                setFromFilterDate();
                setToFilterDate();
              }}>{text("Clear")}</Button>
            </div>
            <Form.Field></Form.Field>
          </Form.Group>
          <PriceElementsList state={state} pricings={Object.values(groupedPricingElements)} className="supplement">
              {filterDateRangesByDateRange(Object.values(groupedPricingElements), fromFilterDate, toFilterDate)
                ?.filter(x => x.IsActive && !x.IsOverride)
                .sort((a, b) => a.StartDate < b.StartDate ? -1 : 1)
                .reduce((acc, group) => {
                    acc.push(group);
                    let originI = acc.findIndex(x => x.isOrigin);
                    let newI = acc.findIndex(x => x.StartDate === null && x.EndDate === null);
                    if (originI !== -1 && newI !== -1) {
                      acc.splice(originI + 1, 0, acc.splice(newI, 1)[0]);
                      delete acc[originI].isOrigin;
                    }
                    return acc;
                }, [])
                .map((group) => {
                  const isDerived = group.RateId === state.BaseRateId;
                  return (
                    <List.Item key={group.Id || group.TempId}>
                      <PricingElementPanel
                        pricing={group}
                        onDelete={() => onDelete(group)}
                        onSave={onSaveGroup}
                        isDerived={isDerived}
                        onCopy={onCopy}
                        setPricings={setGroupedPricingElements}
                      >
                      <Table attached celled color={group.IsNew ? "red" : isDerived ? "green" : undefined}>
                        <Table.Header>  
                          <Table.Row>
                          {
                            tableHeaders.map((header, index) => (
                              <Table.HeaderCell key={index} >
                                {header}
                              </Table.HeaderCell>
                            ))
                          }
                          </Table.Row>
                        </Table.Header>
                        {
                            roomTypes
                            .toSorted((a, b) => a.Name.localeCompare(b.Name)) // Sort by Name in ascending order
                            .map((roomType) => {
                            let fittingRPE = group.RPEs.find(rpe => rpe.EntityId === roomType.Id);
                            return <Table.Row key={roomType.Id}>
                              <Table.Cell>
                                {roomType.Name} ({roomType.ShortName})
                              </Table.Cell>
                              <Table.Cell>
                                <div className="checkbox-wrapper">
                                  <Popup
                                    content={`${text("Amount")}/${text("Percent")}`}
                                    trigger={<span>{`${text("Amount")[0]} / ${text("Percent")[0]}`}</span>}
                                  />
                                  <CheckBoxToggle
                                    onClick={() => toggleIsPercent(fittingRPE)}
                                    checked={fittingRPE?.IsPercent ?? false}
                                    disabled={fittingRPE === undefined}
                                  />
                                </div>
                              </Table.Cell>
                              {cellDesignations.map(designationId => <Table.Cell key={designationId}>
                                <NumericInput
                                  selectOnFocus
                                  buttonPlacement="none"
                                  value={getPrice(fittingRPE, designationId)}
                                  onChangeComplete={(value) => changePriceInRoomType(value, designationId, fittingRPE, group, roomType)}
                                  max={fittingRPE?.IsPercent ? 100 : undefined}
                                />
                              </Table.Cell>)}
                            </Table.Row>
                          })}
                      </Table>
                      </PricingElementPanel>
                    </List.Item>
                  );
                })}
            </PriceElementsList>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button negative onClick={handleClose}>
          {text("Close")}
        </Button>
        <Button positive onClick={() => onSave(true)}>
          {text("Saveandclose")}
        </Button>
        <Button positive onClick={() => onSave()}>
          {text("Save")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

SupplementPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  roomTypes: PropTypes.array,
  standardRoomTypeId: PropTypes.number,
  Packages: PropTypes.array,
  updateRPEs: PropTypes.func,
  rateElementName: PropTypes.string
};
